import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
// IMPORT Plus, Trash2, and Edit2 icons from lucide-react
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { confirmToast } from '../../utils/confirmToast';

// Interface for the client data
interface Client {
  _id: string;
  brandName: string;
  brandLogo: string;
  brandLink: string;
}

const ClientSection: React.FC = () => {
  const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const [showAddForm, setShowAddForm] = useState(false);
  const [newClient, setNewClient] = useState({
    brandName: '',
    brandLogo: '',
    brandLink: '',
  });

  // ADDED: State for editing modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedEditFile, setSelectedEditFile] = useState<File | null>(null);
  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setIsLoading(true);
        setError('');
        const res = await fetch(`${API_BASE_URL}/api/clients`);
        const contentType = res.headers.get('content-type') || '';
        let data: any = null;
        if (contentType.includes('application/json')) {
          try {
            data = await res.json();
          } catch (_) {
            data = null;
          }
        }
        if (!res.ok) {
          const message = (data && (data.message || data.error)) || `Request failed (${res.status})`;
          throw new Error(message);
        }
        if (!data || data.success === false || !Array.isArray(data.clients)) {
          throw new Error('Invalid response format while fetching clients');
        }
        setClients(data.clients);
      } catch (err: any) {
        setError(err.message || 'Failed to load clients');
      } finally {
        setIsLoading(false);
      }
    };
    fetchClients();
  }, []);

  // Handler to add a new client logo
  // const handleAddClient = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   try {
  //     setError('');
  //     const res = await fetch(`${API_BASE_URL}/api/clients`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(newClient),
  //     });
  //     const data = await res.json();
  //     if (!res.ok) {
  //       throw new Error(data.message || 'Failed to add client');
  //     }
  //     if (!data || data.success === false || !data.client) {
  //       throw new Error('Invalid response format while creating client');
  //     }
  //     setClients((prev) => [data.client, ...prev]);
  //     setNewClient({ brandName: '', brandLogo: '', brandLink: '' });
  //     setShowAddForm(false);
  //   } catch (err: any) {
  //     setError(err.message || 'Failed to add client');
  //   }
  // };


  const handleAddClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent duplicates
    setIsSubmitting(true);
    setError('');
    let finalLogoUrl = newClient.brandLogo;

    try {
      // 1. Handle image upload if a file is selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('image', selectedFile);
        const uploadRes = await fetch(`${API_BASE_URL}/api/upload`, { method: 'POST', body: formData });
        const uploadData = await uploadRes.json();
        if (!uploadData.success) throw new Error(uploadData.message || 'Image upload failed');
        finalLogoUrl = uploadData.imageUrl;
      }

      // 2. Submit client data with the final logo URL
      const clientData = { ...newClient, brandLogo: finalLogoUrl };
      const res = await fetch(`${API_BASE_URL}/api/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clientData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to add client');

      setClients((prev) => [data.client, ...prev]);
      setNewClient({ brandName: '', brandLogo: '', brandLink: '' });
      setSelectedFile(null); // Reset file state
      setShowAddForm(false);
    } catch (err: any) {
      setError(err.message || 'Failed to add client');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ADDED: Handler to delete a client
  const handleDeleteClient = async (id: string) => {
    const confirmed = await confirmToast('Are you sure you want to delete this client?', {
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    try {
      setError('');
      const res = await fetch(`${API_BASE_URL}/api/clients/${id}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to delete client');
      }
      setClients(clients.filter(c => c._id !== id));
      toast.success('Client deleted');
    } catch (err: any) {
      setError(err.message || 'Failed to delete client');
      toast.error(err.message || 'Failed to delete client');
    }
  };

  // ADDED: Handler to update a client
  // const handleUpdateClient = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   if (!editingClient) return;

  //   try {
  //     setError('');
  //     const res = await fetch(`${API_BASE_URL}/api/clients/${editingClient._id}`, {
  //       method: 'PUT',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(editingClient),
  //     });
  //     const data = await res.json();
  //     if (!res.ok) {
  //       throw new Error(data.message || 'Failed to update client');
  //     }
  //     setClients(clients.map(c => c._id === editingClient._id ? data.client : c));
  //     closeEditModal();
  //   } catch (err: any) {
  //     setError(err.message || 'Failed to update client');
  //   }
  // };


  const handleUpdateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingClient) return;
    if (isSubmitting) return; // Prevent duplicates

    setIsSubmitting(true);
    setError('');
    let finalLogoUrl = editingClient.brandLogo;

    // 1. Handle image upload if a new file is selected
    if (selectedEditFile) {
      const formData = new FormData();
      formData.append('image', selectedEditFile);
      try {
        const uploadRes = await fetch(`${API_BASE_URL}/api/upload`, { method: 'POST', body: formData });
        const uploadData = await uploadRes.json();
        if (!uploadData.success) throw new Error(uploadData.message || 'Image upload failed');
        finalLogoUrl = uploadData.imageUrl;
      } catch (err: any) {
        setError(err.message);
        return;
      }
    }

    // 2. Submit updated client data
    try {
      const updatedData = { ...editingClient, brandLogo: finalLogoUrl };
      const res = await fetch(`${API_BASE_URL}/api/clients/${editingClient._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update client');

      setClients(clients.map(c => c._id === editingClient._id ? data.client : c));
      setSelectedEditFile(null); // Reset file state
      closeEditModal();
    } catch (err: any) {
      setError(err.message || 'Failed to update client');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ADDED: Functions to open/close edit modal
  const openEditModal = (client: Client) => {
    setEditingClient(client);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingClient(null);
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="font-poppins text-3xl font-bold text-gray-900">Our Clients</h1>
          <p className="font-inter text-gray-600 mt-1">We are proud to partner with leading brands</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddForm(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 hover:bg-primary/90 transition-colors w-full sm:w-auto"
        >
          <Plus className="w-4 h-4" />
          <span className="font-inter font-medium">Add Client</span>
        </motion.button>
      </motion.div>

      {/* Add Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
        >
          <h3 className="font-poppins text-lg font-semibold mb-4">Add New Client</h3>
          <form onSubmit={handleAddClient} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Brand Name</label>
              <input type="text" value={newClient.brandName} onChange={(e) => setNewClient({ ...newClient, brandName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" required />
            </div>
            <div>
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Website Link</label>
              <input type="url" value={newClient.brandLink} onChange={(e) => setNewClient({ ...newClient, brandLink: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" required />
            </div>
            {/* <div className="md:col-span-2">
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Brand Logo URL</label>
              <input type="url" value={newClient.brandLogo} onChange={(e) => setNewClient({ ...newClient, brandLogo: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" required />
            </div> */}

            {/* REPLACEMENT for Brand Logo URL input in Add Form */}
            <div className="md:col-span-2">
              <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Brand Logo</label>
              <div className="flex items-center space-x-2">
                <input
                  type="url"
                  placeholder="Paste Logo URL"
                  value={newClient.brandLogo}
                  onChange={(e) => setNewClient({ ...newClient, brandLogo: e.target.value })}
                  className="flex-grow px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <span className="text-gray-500">OR</span>
                <label className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-inter cursor-pointer">
                  Upload
                  <input type="file" onChange={(e) => setSelectedFile(e.target.files ? e.target.files[0] : null)} className="hidden" accept="image/*" />
                </label>
              </div>
              {selectedFile && <p className="text-sm text-gray-600 mt-2">File selected: {selectedFile.name}</p>}
            </div>

            <div className="md:col-span-2 flex space-x-3">
              <button type="submit" disabled={isSubmitting} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors font-inter disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? 'Adding...' : 'Add Client'}
              </button>
              <button type="button" onClick={() => setShowAddForm(false)} disabled={isSubmitting} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors font-inter disabled:opacity-50 disabled:cursor-not-allowed">Cancel</button>
            </div>
          </form>
        </motion.div>
      )}

      {/* ADDED: Edit Modal */}
      {isEditModalOpen && editingClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 w-full max-w-lg"
          >
            <h3 className="font-poppins text-lg font-semibold mb-4">Edit Client</h3>
            <form onSubmit={handleUpdateClient} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Brand Name</label>
                <input type="text" value={editingClient.brandName} onChange={(e) => setEditingClient({ ...editingClient, brandName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" required />
              </div>
              <div>
                <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Brand Link</label>
                <input type="url" value={editingClient.brandLink} onChange={(e) => setEditingClient({ ...editingClient, brandLink: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" required />
              </div>
              {/* <div className="md:col-span-2">
                <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Brand Logo URL</label>
                <input type="url" value={editingClient.brandLogo} onChange={(e) => setEditingClient({ ...editingClient, brandLogo: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" required />
              </div> */}

              {/* REPLACEMENT for Brand Logo URL input in Edit Modal */}
              <div className="md:col-span-2">
                <label className="block font-inter text-sm font-medium text-gray-700 mb-2">Brand Logo URL</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="url"
                    placeholder="Paste Logo URL"
                    value={editingClient.brandLogo}
                    onChange={(e) => setEditingClient({ ...editingClient, brandLogo: e.target.value })}
                    className="flex-grow px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <span className="text-gray-500">OR</span>
                  <label className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors font-inter cursor-pointer">
                    Upload
                    <input type="file" onChange={(e) => setSelectedEditFile(e.target.files ? e.target.files[0] : null)} className="hidden" accept="image/*" />
                  </label>
                </div>
                {selectedEditFile && <p className="text-sm text-gray-600 mt-2">File selected: {selectedEditFile.name}</p>}
              </div>

              <div className="md:col-span-2 flex space-x-3">
                <button type="submit" disabled={isSubmitting} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors font-inter disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
                <button type="button" onClick={closeEditModal} disabled={isSubmitting} className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors font-inter disabled:opacity-50 disabled:cursor-not-allowed">Cancel</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg p-3 text-sm">{error}</div>
      )}

      {/* Client Logos */}
      <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8 md:gap-x-20 py-8">
        {isLoading && clients.length === 0 && (
          <div className="text-gray-500 font-inter">Loading clients...</div>
        )}
        {clients.map((client, index) => (
          // UPDATED: Wrapped in a div for positioning context
          <motion.div
            key={client._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="relative group"
          >
            {/* ADDED: Edit and Delete buttons */}
            <div className="absolute -top-3 -right-3 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <button onClick={() => openEditModal(client)} className="bg-blue-500 text-white p-1 rounded hover:bg-blue-600 transition-colors" aria-label="Edit Client">
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={() => handleDeleteClient(client._id)} className="bg-red-500 text-white p-1 rounded hover:bg-red-600 transition-colors" aria-label="Delete Client">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <a
              href={client.brandLink}
              target="_blank"
              rel="noopener noreferrer"
              className="focus:outline-none"
              aria-label={`Visit ${client.brandName}'s website`}
            >
              <img
                src={client.brandLogo}
                alt={`${client.brandName} logo`}
                className="h-10 md:h-12 object-contain"
              />
            </a>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ClientSection;
